import React, { useEffect, useState } from "react";
import commonStyles from "../../common/CommonStyles.module.css";
import s from "./RolesManagement.module.css";

const isSystemRole = (role) => Boolean(role?.systemRole);
const isSystemPrivilege = (privilege) => Boolean(privilege?.systemPrivilege);
const displayName = (entity) => entity?.displayName || entity?.name || "";

const RolesManagement = () => {
  const [roles, setRoles] = useState([]);
  const [privileges, setPrivileges] = useState([]);
  const [editingRole, setEditingRole] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [roleDisplayName, setRoleDisplayName] = useState("");
  const [selectedPrivilegeIds, setSelectedPrivilegeIds] = useState([]);
  const [errorMessage, setErrorMessage] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    fetchData();
  }, []);

  const extractErrorMessage = async (response, fallback) => {
    try {
      const data = await response.json();
      return data?.message || data?.error || fallback;
    } catch {
      return fallback;
    }
  };

  const fetchData = async () => {
    setIsLoading(true);
    setErrorMessage("");
    try {
      const [rolesResponse, privilegesResponse] = await Promise.all([
        fetch("/api/admin/roles"),
        fetch("/api/admin/privileges"),
      ]);

      if (!rolesResponse.ok) {
        throw new Error(
          await extractErrorMessage(rolesResponse, "Ошибка при загрузке ролей")
        );
      }
      if (!privilegesResponse.ok) {
        throw new Error(
          await extractErrorMessage(
            privilegesResponse,
            "Ошибка при загрузке привилегий"
          )
        );
      }

      const rolesData = await rolesResponse.json();
      const privilegesData = await privilegesResponse.json();
      setRoles(
        Array.isArray(rolesData)
          ? rolesData.filter((role) => !isSystemRole(role))
          : []
      );
      setPrivileges(
        Array.isArray(privilegesData)
          ? privilegesData.filter((privilege) => !isSystemPrivilege(privilege))
          : []
      );
    } catch (error) {
      console.error("Error fetching roles data:", error);
      setErrorMessage(error.message || "Ошибка при загрузке данных");
      setRoles([]);
      setPrivileges([]);
    } finally {
      setIsLoading(false);
    }
  };

  const openCreateModal = () => {
    setEditingRole(null);
    setRoleDisplayName("");
    setSelectedPrivilegeIds([]);
    setErrorMessage("");
    setIsModalOpen(true);
  };

  const openEditModal = (role) => {
    setEditingRole(role);
    setRoleDisplayName(role.displayName || "");
    setSelectedPrivilegeIds(
      Array.isArray(role.privileges)
        ? role.privileges.map((privilege) => privilege.id)
        : []
    );
    setErrorMessage("");
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setEditingRole(null);
    setRoleDisplayName("");
    setSelectedPrivilegeIds([]);
  };

  const togglePrivilege = (privilegeId) => {
    setSelectedPrivilegeIds((prev) =>
      prev.includes(privilegeId)
        ? prev.filter((id) => id !== privilegeId)
        : [...prev, privilegeId]
    );
  };

  const handleDelete = async (role) => {
    if (isSystemRole(role)) {
      setErrorMessage("Системную роль нельзя удалить");
      return;
    }
    if (!window.confirm(`Удалить роль «${displayName(role)}»?`)) return;

    setErrorMessage("");
    try {
      const response = await fetch(`/api/admin/roles/${role.id}`, {
        method: "DELETE",
      });
      if (!response.ok) {
        throw new Error(
          await extractErrorMessage(response, "Ошибка при удалении роли")
        );
      }
      fetchData();
    } catch (error) {
      console.error("Error deleting role:", error);
      setErrorMessage(error.message || "Ошибка при удалении роли");
    }
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setErrorMessage("");

    const displayNameValue = roleDisplayName.trim();
    if (!displayNameValue) {
      setErrorMessage("Введите название роли");
      return;
    }

    const payload = {
      displayName: displayNameValue,
      privilegeIds: selectedPrivilegeIds,
    };

    const url = editingRole
      ? `/api/admin/roles/${editingRole.id}`
      : "/api/admin/roles";
    const method = editingRole ? "PUT" : "POST";

    setIsSaving(true);
    try {
      const response = await fetch(url, {
        method,
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      if (!response.ok) {
        throw new Error(
          await extractErrorMessage(response, "Ошибка при сохранении роли")
        );
      }
      closeModal();
      fetchData();
    } catch (error) {
      console.error("Error saving role:", error);
      setErrorMessage(error.message || "Ошибка при сохранении роли");
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className={commonStyles.pageContainer}>
      <div className={commonStyles.contentContainer}>
        <div className={commonStyles.content}>
          <h2 className={s.title}>Управление ролями</h2>

          <div className={s.actionsTop}>
            <button className={s.addButton} onClick={openCreateModal}>
              <span className={s.plus}>+</span> Создать роль
            </button>
          </div>

          {isLoading ? (
            <div className={s.centerMessage}>Загрузка...</div>
          ) : (
            <div className={s.tableContainer}>
              <table className={s.table}>
                <thead>
                  <tr>
                    <th>Название</th>
                    <th>Привилегии</th>
                    <th>Действия</th>
                  </tr>
                </thead>
                <tbody>
                  {roles.map((role) => (
                    <tr key={role.id} className={s.tableRow}>
                      <td className={s.nameCell}>{displayName(role)}</td>
                      <td className={s.privilegesCell}>
                        {role.privileges && role.privileges.length ? (
                          <div className={s.privilegesList}>
                            {role.privileges.map((privilege) => (
                              <span
                                key={privilege.id}
                                className={s.privilegeTag}
                                title={privilege.name}
                              >
                                {displayName(privilege)}
                              </span>
                            ))}
                          </div>
                        ) : (
                          <span className={s.emptyPrivileges}>—</span>
                        )}
                      </td>
                      <td className={s.actionsCell}>
                        <button
                          className={s.editButton}
                          onClick={() => openEditModal(role)}
                        >
                          Редактировать
                        </button>
                        <button
                          className={s.deleteButton}
                          onClick={() => handleDelete(role)}
                        >
                          Удалить
                        </button>
                      </td>
                    </tr>
                  ))}
                  {!roles.length && (
                    <tr>
                      <td colSpan={3} className={s.emptyRow}>
                        Пока нет ролей
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          )}

          {errorMessage && !isModalOpen && (
            <div className={s.errorBox}>{errorMessage}</div>
          )}

          {isModalOpen && (
            <div className={s.modal}>
              <div className={s.modalContent}>
                <h3 className={s.modalTitle}>
                  {editingRole ? "Редактирование роли" : "Новая роль"}
                </h3>
                <form onSubmit={handleSubmit}>
                  <div className={s.formSection}>
                    <label className={s.label}>Название:</label>
                    <input
                      value={roleDisplayName}
                      onChange={(e) => setRoleDisplayName(e.target.value)}
                      className={s.input}
                      placeholder="Администратор"
                      required
                    />
                  </div>

                  <div className={s.formSection}>
                    <label className={s.label}>Привилегии:</label>
                    {privileges.length ? (
                      <div className={s.checkboxList}>
                        {privileges.map((privilege) => (
                          <label
                            key={privilege.id}
                            className={s.checkboxItem}
                            title={privilege.name}
                          >
                            <input
                              type="checkbox"
                              checked={selectedPrivilegeIds.includes(
                                privilege.id
                              )}
                              onChange={() => togglePrivilege(privilege.id)}
                            />
                            <span>{displayName(privilege)}</span>
                          </label>
                        ))}
                      </div>
                    ) : (
                      <div className={s.hint}>Список привилегий пуст</div>
                    )}
                  </div>

                  {errorMessage && (
                    <div className={s.errorBox}>{errorMessage}</div>
                  )}

                  <div className={s.modalActions}>
                    <button
                      type="submit"
                      className={s.saveButton}
                      disabled={isSaving}
                    >
                      {isSaving ? "Сохранение..." : "Сохранить"}
                    </button>
                    <button
                      type="button"
                      className={s.cancelButton}
                      onClick={() => {
                        closeModal();
                        setErrorMessage("");
                      }}
                      disabled={isSaving}
                    >
                      Отмена
                    </button>
                  </div>
                </form>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default RolesManagement;
